import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  browserLoginHandoffUrl,
  isBrowserAuthHandoff,
  isDesktopShell,
  openInSystemBrowser,
} from "@/lib/desktop-shell";
import { signInWithPasskey } from "@/lib/passkey-client";

export function PasswordAuthForm({
  mode,
  redirectTo,
  defaultEmail = "",
}: {
  mode: "login" | "signup";
  redirectTo: string;
  defaultEmail?: string;
}) {
  const { signInPassword, signUpPassword, applySession } = useAuth();
  const navigate = useNavigate();
  const emailRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") ?? "").trim();
    const password = String(fd.get("password") ?? "");
    const name = String(fd.get("name") ?? "").trim();
    setPending(true);
    setError(null);
    try {
      const next =
        mode === "signup"
          ? await signUpPassword({ email, password, name, redirectTo })
          : await signInPassword({ email, password, redirectTo });
      if (isBrowserAuthHandoff()) {
        return;
      }
      navigate(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not sign in.");
      setPending(false);
    }
  }

  async function onPasskey() {
    const email = emailRef.current?.value.trim() ?? "";
    if (!email) {
      setError("Enter your email, then use your passkey.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      if (isDesktopShell() && !isBrowserAuthHandoff()) {
        const url = browserLoginHandoffUrl(window.location.origin, {
          email,
          next: redirectTo,
        });
        const opened = await openInSystemBrowser(url);
        if (!opened) {
          window.location.href = url;
        }
        setError("Finish with your passkey in the browser window, then this app will open.");
        setPending(false);
        return;
      }
      const res = await signInWithPasskey(email);
      applySession({
        token: res.token,
        user: res.user,
        redirectTo: res.redirectTo || redirectTo,
      });
      if (isBrowserAuthHandoff()) {
        return;
      }
      navigate(res.redirectTo || redirectTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Passkey sign-in failed.");
      setPending(false);
    }
  }

  return (
    <form className="space-y-3" onSubmit={(e) => void onSubmit(e)}>
      {mode === "signup" ? (
        <label className="block text-sm">
          <span className="font-medium text-slate-800">Your name</span>
          <input
            name="name"
            autoComplete="name"
            placeholder="Alex Broker"
            className="mt-1 w-full rounded-xl border border-white/70 bg-white/45 px-3 py-2 backdrop-blur-md focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600/25"
          />
        </label>
      ) : null}
      <label className="block text-sm">
        <span className="font-medium text-slate-800">Work email</span>
        <input
          ref={emailRef}
          name="email"
          type="email"
          required
          defaultValue={defaultEmail}
          autoComplete="username webauthn"
          placeholder="you@brokerage.com"
          className="mt-1 w-full rounded-xl border border-white/70 bg-white/45 px-3 py-2 backdrop-blur-md focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600/25"
        />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-slate-800">Password</span>
        <input
          name="password"
          type="password"
          required={mode === "signup"}
          minLength={mode === "signup" ? 10 : undefined}
          autoComplete={mode === "signup" ? "new-password" : "current-password"}
          className="mt-1 w-full rounded-xl border border-white/70 bg-white/45 px-3 py-2 backdrop-blur-md focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600/25"
        />
      </label>
      {mode === "signup" ? (
        <p className="text-xs text-slate-500">At least 10 characters.</p>
      ) : null}
      <button
        type="submit"
        disabled={pending}
        className="w-full rounded-xl bg-teal-700 px-4 py-3 text-sm font-semibold text-white shadow-md shadow-teal-700/20 hover:bg-teal-800 disabled:opacity-60"
      >
        {pending
          ? mode === "signup"
            ? "Creating account…"
            : "Signing in…"
          : mode === "signup"
            ? "Create account"
            : "Sign in"}
      </button>
      {mode === "login" ? (
        <button
          type="button"
          disabled={pending}
          onClick={() => void onPasskey()}
          className="w-full rounded-xl border border-slate-300 bg-white/50 px-4 py-3 text-sm font-semibold text-slate-800 disabled:opacity-60"
        >
          {pending ? "Waiting for passkey…" : "Sign in with a passkey"}
        </button>
      ) : null}
      {error ? (
        <p className="text-sm text-rose-600" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}
